import type { Permission } from '@field-support/shared';
import { type ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useAuth } from '@/entities/session';
import { LoginPage } from '@/pages/login';
import { Loading, Notice } from '@/shared/ui';
import { Layout } from './Layout';
import { NAV_ITEMS } from './navigation';
import { PAGES, UiGalleryPage } from './pages';

/** Renders children only when the user holds ANY of the given permissions. */
export function Guard({ any, children }: { any: Permission[]; children: ReactNode }) {
  const { canAny } = useAuth();
  const { t } = useTranslation();
  if (any.length > 0 && !canAny(...any)) return <Notice tone="partial">{t('common.forbidden')}</Notice>;
  return <>{children}</>;
}

/** Lands on the first page this user may see. */
function Home() {
  const { canAny } = useAuth();
  const { t } = useTranslation();
  const first = NAV_ITEMS.find((item) => item.permissions.length === 0 || canAny(...item.permissions));
  if (!first) return <Notice>{t('common.welcome')}</Notice>;
  return <Navigate to={first.path} replace />;
}

export function AppRoutes() {
  const { status } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  if (status === 'loading') {
    return (
      <div aria-busy="true" style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', font: 'var(--text-body)' }}>
        {t('common.loading')}
      </div>
    );
  }

  if (status === 'anonymous') {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: location.pathname + location.search }} />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to={(location.state as { from?: string } | null)?.from ?? '/'} replace />} />
      <Route element={<Layout />}>
        <Route index element={<Home />} />
        {NAV_ITEMS.map((item) => {
          const Page = PAGES[item.path];
          if (!Page) return null;
          return (
            <Route
              key={item.path}
              path={item.path}
              element={
                <Guard any={item.permissions}>
                  <Suspense fallback={<Loading />}>
                    <Page />
                  </Suspense>
                </Guard>
              }
            />
          );
        })}
        {UiGalleryPage && (
          <Route
            path="/ui-gallery"
            element={
              <Suspense fallback={<Loading />}>
                <UiGalleryPage />
              </Suspense>
            }
          />
        )}
        {/* One route per NAV_ITEMS entry (lazy, from ./pages), each behind the Guard of its nav permissions. */}
        <Route path="*" element={<Notice tone="partial">{t('common.notFound')}</Notice>} />
      </Route>
    </Routes>
  );
}
